/**
 * The domain-setup screen's decisions, separated from its markup — `screens.md` §8.
 *
 * Everything here is a pure function of a `domains` row, so each of the screen's states is
 * a **static rendering** `apps/app`'s suite can drive. What that suite structurally cannot
 * do is traverse between them: it is `renderToStaticMarkup` with jsdom deliberately absent,
 * so it fires no events and runs no effects. The add → poll → `verifying` traversal is
 * `e2e/`'s, and a static render of what lies behind a gate is not evidence the gate opens.
 */
import {
  PERMANENCE_SENTENCE,
  REMOVED_DOMAIN_STATUS,
  SUSTAINED_PROBE_FAILURES,
  type ActiveDomainStatus,
  type DomainStatus,
  type DomainSummary,
} from "@packagepage/shared";
import type { PillValue } from "@packagepage/ui";

import type { DomainProgress } from "./domain-timeline.js";

/**
 * The seven states §8 requires, as one discriminant.
 *
 * Derived from facts on the row, never from an index into `DOMAIN_STATUSES` — that set is
 * not a ladder (U11's 🔴), and two of these states share a status and differ only on
 * `first_live_at`.
 */
export const DOMAIN_SCREEN_STATES = [
  /** A · no active domain. The explainer that sells ownership. */
  "pre-setup",
  /** B · the main screen: added, waiting for the record to appear. */
  "waiting",
  /** C · live, with the moment it first served surfaced for good. */
  "live",
  /** D · a record exists and points elsewhere — found-vs-wanted. */
  "error-never-served",
  /** E · apex steered, decided by the hostname's shape rather than by its status. */
  "apex-steered",
  /** F · gated (Starter) — a plan below Starter with no domain in service. `DomainGate`. */
  "gated",
  /** G · ever-live and failing now. "You can still make print files." */
  "ever-live-failing",
] as const;

export type DomainScreenState = (typeof DOMAIN_SCREEN_STATES)[number];

/** A registrable apex — `brand.com`, not `id.brand.com`. Mirrors the op's own predicate. */
export function isApexHostname(hostname: string): boolean {
  return hostname.split(".").length === 2;
}

/**
 * Which of §8's states one row is in.
 *
 * The two `error` arms are the distinction §8.1 is built on and `acceptance-tests.md` names
 * outright — *"domain-never-served and domain-ever-live-but-failing cannot be mistaken for
 * each other. The first is a wall with two doors; the second is a speed bump with a
 * seatbelt."* They differ only on `first_live_at`, which is why that column is write-once.
 */
export function screenStateFor(domain: DomainSummary): DomainScreenState {
  if (isApexHostname(domain.hostname) && domain.status === "pending_dns") {
    return "apex-steered";
  }
  switch (domain.status) {
    case "pending_dns":
    case "verifying":
      return "waiting";
    // `live` AND failing for the sustained window — state G's first reachable arm. Nothing
    // demotes a live row (Q1, 2026-09-17), so the counter is the only thing that can say a
    // serving domain has stopped serving. The `error` arm below stays for a future demotion
    // that Q1 does not authorise: both reach G, and deleting either loses a case.
    case "live":
      return domain.consecutive_probe_failures >= SUSTAINED_PROBE_FAILURES
        ? "ever-live-failing"
        : "live";
    case "error":
      return domain.first_live_at === null ? "error-never-served" : "ever-live-failing";
    // A removed row never reaches this screen — `listDomains` excludes it.
    case "removed":
      return "pre-setup";
  }
}

/**
 * Database vocabulary → display vocabulary.
 *
 * `PILL_TONES.domain` says `waiting` where `DOMAIN_STATUSES` says `pending_dns`, and that is
 * a mapping rather than a mistake on either side: pill families carry the shopper's word
 * (`PILL_TONES.product` says `unavailable` for a tombstone). A total record, so a new status
 * member is a compile error here rather than a row that renders untoned.
 */
export const DOMAIN_STATUS_PILL: Record<ActiveDomainStatus, PillValue<"domain">> = {
  pending_dns: "waiting",
  verifying: "verifying",
  live: "live",
  error: "error",
};

/**
 * The pill for any status, total so the screen needs no narrowing.
 *
 * A `removed` row never reaches this screen — `listDomains` excludes it — so the map above
 * deliberately has no entry for it. If one ever arrives, `error` is the honest tone: it
 * means something is wrong, and the something is ours.
 */
export function pillFor(status: DomainStatus): PillValue<"domain"> {
  return status === REMOVED_DOMAIN_STATUS ? "error" : DOMAIN_STATUS_PILL[status];
}

/** The facts {@link DomainProgress} needs, read off a row. */
export function progressOf(domain: DomainSummary): DomainProgress {
  const everServed = domain.first_live_at !== null;
  // **A timeline records progress REACHED, not truth NOW** — `done` means a step was passed
  // and `screens.md` §8 says the fourth step's timestamp is "shown for good once reached".
  // So a domain that has ever served HAS found its record and HAS had a certificate,
  // whatever its status says today.
  //
  // The earlier version derived both from `status === "live"` alone. It never over-claimed,
  // which was the instinct, but it UNDER-claimed on ever-live-but-failing — reporting no
  // certificate for a domain that demonstrably had one, on the single state where the
  // customer is already anxious and would read it as the certificate being the problem when
  // the problem is DNS. Two facts, not one: "has a working certificate now" is
  // `status === "live"`; "ever had one" is what a passed timeline step means.
  //
  // `acme_ok` is written by U13's probe, in the same UPDATE as the stamp, and is still not
  // on this wire — a 307 through the public edge is the issuance evidence, so the column
  // adds nothing a `live` status does not already say. `first_live_at` is the
  // stronger evidence anyway: a domain cannot have served without one.
  return {
    recordFound: everServed || domain.status === "verifying" || domain.status === "live",
    certificateIssued: everServed || domain.status === "live",
    everServed,
  };
}

/**
 * "14 minutes ago", "on 14 November".
 *
 * `Intl.RelativeTimeFormat` rather than a dependency, and `en-GB` rather than the browser's
 * locale — `acceptance-tests.md` has the app UI in English while product content is
 * multi-language, so a date following the reader's locale would be the one string on screen
 * that moves. Same call `router.tsx`'s `today()` makes.
 *
 * Past about a week it stops counting and names the date: "6 days ago" is useful, "83 days
 * ago" is arithmetic the reader has to do themselves.
 */
export function relativeTime(iso: string, now: Date): string {
  const then = new Date(iso);
  const seconds = Math.round((then.getTime() - now.getTime()) / 1000);
  const magnitude = Math.abs(seconds);

  if (magnitude >= 7 * 24 * 60 * 60) {
    return `on ${new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long" }).format(then)}`;
  }

  const format = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
  const units: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
    ["day", 24 * 60 * 60],
    ["hour", 60 * 60],
    ["minute", 60],
  ];
  for (const [unit, size] of units) {
    if (magnitude >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, "minute");
}

/**
 * The provider cards — **Cloudflare and a generic "Other", ruled 2026-09-08.**
 *
 * `screens.md` §8's mock draws four tabs and authors steps for **one**. Measured at c144:
 * GoDaddy and Squarespace copy exists in no form anywhere in the handoff, so shipping four
 * would mean **inventing instructions about somebody else's product** — a claim we cannot
 * check and the customer would follow. Two providers with real copy beats four with
 * invented copy.
 *
 * The proxy warning is Cloudflare's and stays: the mock's own rationale calls it *"the
 * single most common self-inflicted failure"*, and it costs one line to prevent.
 *
 * Trigger for the missing two: design supplies the copy, or the first support ticket does.
 */
export interface ProviderGuide {
  readonly id: string;
  readonly label: string;
  readonly steps: readonly string[];
}

export function providerGuides(host: string, target: string): readonly ProviderGuide[] {
  return [
    {
      id: "cloudflare",
      label: "Cloudflare",
      steps: [
        "Open your domain and go to DNS → Records.",
        `Add record, type CNAME, name ${host}, target ${target}.`,
        "Set Proxy status to DNS only — the grey cloud. Proxying breaks the certificate we issue.",
      ],
    },
    {
      id: "other",
      label: "Other",
      steps: [
        "Find the DNS or Records section of whoever manages this domain.",
        `Add a CNAME record with the name ${host} and the target ${target}.`,
        "If your provider offers a proxy, CDN or redirect option on the record, turn it off — the certificate is issued to us directly.",
      ],
    },
  ];
}

/**
 * The sentence a customer needs at the moment removal is tempting.
 *
 * State G is where {@link PERMANENCE_SENTENCE} earns its call site: `first_live_at` is set,
 * so codes carrying this hostname are already printed and in the field, and the reader is
 * looking at a domain that is failing right now. Removing it un-prints nothing. Composed
 * byte for byte from `packages/shared` — `screens.md` §8 requires this sentence and the
 * artwork escape hatch's to be word-identical, and c91 put it there for exactly this screen.
 */
export const EVER_LIVE_FAILING_COPY = {
  headline: "Not answering right now",
  reassurance:
    "Scans are falling back to our address in the meantime, so nothing is dead-ending. " +
    "You can still make print files: this is a live domain having a bad afternoon, not an " +
    "unproven one.",
  permanence: PERMANENCE_SENTENCE,
} as const;
